import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ProjectDataTypes } from '@mono/proto-clients';
import { SpecraBadge, SpecraButton, SpecraEntityMatchConfirm, SpecraFormField, SpecraInput, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toMatchCandidates } from '../../components/SharedRecord/toMatchCandidates';
import { useDebounce } from '../../hooks/useDebounce';
import { deliveryClient } from '../../lib/connect/clients';
import { useTenantId } from '../Delivery/_shared/hooks/useTenantId';
import { useResolvePursuitEntity } from './themeHooks';
const ids = Testids.growthShape01;
/**
 * GRW-37: a saved attendee typed by name waits on a REG-54 match candidate.
 * "Yes, link" / "This one" links the person; "None of these — create new"
 * adds them to Registries; "Decide later" leaves it in the review queue.
 */ function AttendeeMatchPrompt({ participant, captureProjectId, disabled }) {
    const resolve = useResolvePursuitEntity();
    const [declined, setDeclined] = useState(false);
    const [deferred, setDeferred] = useState(false);
    const decide = (decision, registryId)=>{
        if (!captureProjectId) return;
        resolve.mutate({
            captureProjectId,
            linkKind: 'TOUCHPOINT_PARTICIPANT',
            linkId: participant.id,
            decision,
            registryId
        });
    };
    return /*#__PURE__*/ _jsx(SpecraEntityMatchConfirm, {
        kind: "CONTACT",
        size: "xs",
        density: "inline",
        "data-testid": ids.attendeeMatchPrompt,
        readOnly: !captureProjectId,
        disabled: disabled,
        observedLabel: participant.attendeeName || 'This attendee',
        observedContext: participant.organization || undefined,
        candidates: toMatchCandidates(participant.candidates),
        state: resolve.isPending ? 'pending' : resolve.isError ? 'error' : resolve.isSuccess ? 'linked' : deferred ? 'deferred' : declined ? 'declined' : 'ask',
        errorMessage: "Couldn't link this attendee. Try again.",
        createLabel: "None of these — create new",
        onConfirm: (id)=>decide('PICK', id),
        onDecline: ()=>setDeclined(true),
        onCreateNew: ()=>decide('CREATE_NEW'),
        onDefer: ()=>setDeferred(true)
    });
}
export function TouchpointParticipants({ participants, persistedIds, onChange, disabled, captureProjectId }) {
    const tenantId = useTenantId();
    const [search, setSearch] = useState('');
    const [resolvingId, setResolvingId] = useState(null);
    const debouncedSearch = useDebounce(search, 300);
    const people = useQuery({
        queryKey: [
            'touchpoint-people',
            tenantId,
            debouncedSearch
        ],
        enabled: !!search.trim() && search === debouncedSearch,
        queryFn: async ({ signal })=>(await deliveryClient.listContacts({
                companyId: tenantId,
                search: debouncedSearch
            }, {
                signal
            })).contacts
    });
    const linkedPeople = useQuery({
        queryKey: [
            'touchpoint-people',
            tenantId,
            ''
        ],
        enabled: participants.some((p)=>!!p.contactId),
        staleTime: 60000,
        queryFn: async ({ signal })=>(await deliveryClient.listContacts({
                companyId: tenantId,
                search: ''
            }, {
                signal
            })).contacts
    });
    const linkedLabel = (id)=>{
        const person = linkedPeople.data?.find((p)=>p.id === id);
        if (!person) return linkedPeople.isFetching ? 'Loading linked Person…' : 'Linked Person unavailable in People. Original meeting context is retained.';
        return `Linked Person: ${[
            `${person.firstName} ${person.lastName}`.trim(),
            person.governmentOrganizationName || person.companyProfileName || person.organization
        ].filter(Boolean).join(' · ') || 'Unnamed Person'}`;
    };
    const update = (id, patch)=>onChange(participants.map((p)=>p.id === id ? new ProjectDataTypes.TouchpointParticipant({
                ...p,
                ...patch
            }) : p));
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3",
        "data-testid": ids.participants,
        "aria-label": "Participants",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                className: "block",
                children: "Participants"
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                className: "block",
                size: "meta",
                tone: "muted",
                children: "Participation describes this touchpoint. It grants no membership or access. Saved attendee context is retained; remove and add an attendee to correct it."
            }),
            linkedPeople.isError && /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                disabled: disabled,
                onClick: ()=>linkedPeople.refetch(),
                children: "Retry linked People"
            }),
            participants.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                className: "block",
                tone: "muted",
                children: "No participants yet. You can save without them."
            }),
            participants.map((p)=>/*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2 rounded-md border p-3",
                    "data-testid": ids.participant,
                    "data-participant-id": p.id,
                    "data-contact-id": p.contactId,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: p.contactId ? 'info' : 'warning',
                            children: p.contactId ? 'Existing Person linked' : 'Unresolved participant'
                        }),
                        p.contactId && /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            className: "block",
                            size: "meta",
                            children: linkedLabel(p.contactId)
                        }),
                        !p.contactId && persistedIds.has(p.id) && p.registryCandidateId && /*#__PURE__*/ _jsx(AttendeeMatchPrompt, {
                            participant: p,
                            captureProjectId: captureProjectId,
                            disabled: disabled
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Attendee name",
                                    htmlFor: `attendee-name-${p.id}`,
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: `attendee-name-${p.id}`,
                                        "data-testid": ids.name,
                                        value: p.attendeeName,
                                        disabled: disabled || persistedIds.has(p.id),
                                        onChange: (e)=>update(p.id, {
                                                attendeeName: e.target.value
                                            })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Organization / context",
                                    htmlFor: `attendee-org-${p.id}`,
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: `attendee-org-${p.id}`,
                                        "data-testid": ids.organization,
                                        value: p.organization,
                                        disabled: disabled || persistedIds.has(p.id),
                                        onChange: (e)=>update(p.id, {
                                                organization: e.target.value
                                            })
                                    })
                                }),
                                !p.contactId && !persistedIds.has(p.id) && /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Email (optional)",
                                    htmlFor: `attendee-email-${p.id}`,
                                    hint: "With an email we link the existing person, or add them, with no prompt. It is not stored on the touchpoint.",
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: `attendee-email-${p.id}`,
                                        type: "email",
                                        "data-testid": ids.attendeeEmail,
                                        value: p.email,
                                        disabled: disabled,
                                        onChange: (e)=>update(p.id, {
                                                email: e.target.value
                                            })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Job role / meeting context",
                                    htmlFor: `attendee-role-${p.id}`,
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: `attendee-role-${p.id}`,
                                        "data-testid": ids.role,
                                        value: p.participantRole,
                                        disabled: disabled || persistedIds.has(p.id),
                                        onChange: (e)=>update(p.id, {
                                                participantRole: e.target.value
                                            })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Participation",
                                    htmlFor: `attendee-kind-${p.id}`,
                                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        id: `attendee-kind-${p.id}`,
                                        "data-testid": ids.kind,
                                        value: p.participationKind,
                                        disabled: disabled || persistedIds.has(p.id),
                                        onChange: (e)=>update(p.id, {
                                                participationKind: e.target.value
                                            }),
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "Unspecified"
                                            }),
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "customer",
                                                children: "Customer"
                                            }),
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "internal",
                                                children: "Internal"
                                            }),
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "partner",
                                                children: "Partner"
                                            })
                                        ]
                                    })
                                })
                            ]
                        }),
                        p.originalContexts.length > 0 && /*#__PURE__*/ _jsxs("div", {
                            "data-testid": ids.retainedContext,
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    className: "block",
                                    size: "meta",
                                    children: "Retained meeting context"
                                }),
                                p.originalContexts.map((c)=>/*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        className: "block",
                                        size: "meta",
                                        tone: "muted",
                                        children: [
                                            c.name || 'Unnamed attendee',
                                            c.organization,
                                            c.role,
                                            c.kind
                                        ].filter(Boolean).join(' · ')
                                    }, c.attendeeId))
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex gap-2",
                            children: [
                                !p.contactId && /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    disabled: disabled,
                                    "data-testid": ids.resolve,
                                    onClick: ()=>{
                                        setResolvingId(p.id);
                                        setSearch('');
                                    },
                                    children: "Resolve participant"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "ghost",
                                    disabled: disabled,
                                    "data-testid": ids.remove,
                                    onClick: ()=>{
                                        onChange(participants.filter((row)=>row.id !== p.id));
                                        if (resolvingId === p.id) setResolvingId(null);
                                    },
                                    children: "Remove participant"
                                })
                            ]
                        })
                    ]
                }, p.id)),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                disabled: disabled,
                "data-testid": ids.addUnresolved,
                onClick: ()=>onChange([
                        ...participants,
                        new ProjectDataTypes.TouchpointParticipant({
                            id: crypto.randomUUID()
                        })
                    ]),
                children: "Add unresolved participant"
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: resolvingId ? 'Resolve participant — search existing People' : 'Add Person — search existing People',
                htmlFor: "touchpoint-person-search",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    id: "touchpoint-person-search",
                    "data-testid": ids.search,
                    value: search,
                    disabled: disabled,
                    onChange: (e)=>setSearch(e.target.value),
                    placeholder: "Search by name"
                })
            }),
            resolvingId && /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "ghost",
                disabled: disabled,
                onClick: ()=>{
                    setResolvingId(null);
                    setSearch('');
                },
                children: "Cancel resolution"
            }),
            search.trim() && (search !== debouncedSearch || people.isFetching) ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                className: "block",
                "aria-live": "polite",
                children: "Searching People…"
            }) : null,
            search.trim() && search === debouncedSearch && !people.isFetching && (people.isError ? /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "block",
                        children: "Could not search People. Your note and attendees are unchanged."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": ids.retrySearch,
                        onClick: ()=>people.refetch(),
                        children: "Retry search"
                    })
                ]
            }) : people.data?.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                className: "block",
                children: "No existing People found. Add an unresolved participant to continue."
            }) : people.data?.map((person)=>{
                const name = `${person.firstName} ${person.lastName}`.trim();
                const organization = person.governmentOrganizationName || person.companyProfileName || person.organization;
                return /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": ids.personResult,
                    "data-contact-id": person.id,
                    variant: "secondary",
                    disabled: disabled,
                    onClick: ()=>{
                        if (resolvingId) update(resolvingId, {
                            contactId: person.id
                        });
                        else onChange([
                            ...participants,
                            new ProjectDataTypes.TouchpointParticipant({
                                id: crypto.randomUUID(),
                                contactId: person.id,
                                attendeeName: name,
                                organization
                            })
                        ]);
                        setResolvingId(null);
                        setSearch('');
                    },
                    children: [
                        name,
                        organization
                    ].filter(Boolean).join(' · ') || 'Unnamed Person'
                }, person.id);
            }))
        ]
    });
}
